import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { LoadingContainer } from '../components/ui/Spinner';
import { PlusIcon, PencilIcon, TrashIcon, ArrowTrendingUpIcon, ArrowTrendingDownIcon, MinusIcon } from '@heroicons/react/24/outline';
import { formatCurrency } from '../utils/formatting';

const SEVERITY_OPTIONS = [
    { value: 'low', label: 'Low Impact', color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-100 dark:bg-blue-900/30' },
    { value: 'medium', label: 'Medium Impact', color: 'text-yellow-600 dark:text-yellow-400', bg: 'bg-yellow-100 dark:bg-yellow-900/30' },
    { value: 'high', label: 'High Impact', color: 'text-orange-600 dark:text-orange-400', bg: 'bg-orange-100 dark:bg-orange-900/30' },
    { value: 'critical', label: 'Critical', color: 'text-red-600 dark:text-red-400', bg: 'bg-red-100 dark:bg-red-900/30' },
];

const STATUS_OPTIONS = [
    { value: 'identified', label: 'Identified', color: 'text-gray-600 dark:text-gray-400', bg: 'bg-gray-100 dark:bg-gray-700' },
    { value: 'working_on', label: 'Working On', color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-100 dark:bg-blue-900/30' },
    { value: 'improved', label: 'Improved', color: 'text-green-600 dark:text-green-400', bg: 'bg-green-100 dark:bg-green-900/30' },
    { value: 'fixed', label: 'Fixed', color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-100 dark:bg-emerald-900/30' },
];

function LeaksPage() {
    const [leaks, setLeaks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [editingLeak, setEditingLeak] = useState(null);
    const [deleteLeakId, setDeleteLeakId] = useState(null);

    // Form state
    const [form, setForm] = useState({
        name: '',
        description: '',
        category: '',
        severity: 'medium',
        status: 'identified',
        symptoms: [],
        fix: '',
        cue: '',
    });
    const [symptomInput, setSymptomInput] = useState('');

    useEffect(() => {
        fetchLeaks();
    }, []);

    const fetchLeaks = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get('/api/leaks/');
            setLeaks(response.data?.results || response.data || []);
        } catch (err) {
            console.error('Failed to fetch leaks:', err);
            setError('Failed to load leaks');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingLeak) {
                await axios.patch(`/api/leaks/${editingLeak.id}/`, form);
            } else {
                await axios.post('/api/leaks/', form);
            }
            fetchLeaks();
            handleCancel();
        } catch (err) {
            console.error('Failed to save leak:', err);
            setError(err.response?.data?.name?.[0] || 'Failed to save leak');
        }
    };

    const handleEdit = (leak) => {
        setEditingLeak(leak);
        setForm({
            name: leak.name,
            description: leak.description || '',
            category: leak.category || '',
            severity: leak.severity,
            status: leak.status,
            symptoms: leak.symptoms || [],
            fix: leak.fix || '',
            cue: leak.cue || '',
        });
        setSymptomInput('');
        setShowAddModal(true);
    };

    const handleCancel = () => {
        setShowAddModal(false);
        setEditingLeak(null);
        setForm({
            name: '',
            description: '',
            category: '',
            severity: 'medium',
            status: 'identified',
            symptoms: [],
            fix: '',
            cue: '',
        });
        setSymptomInput('');
        setError(null);
    };

    const handleDelete = async () => {
        if (!deleteLeakId) return;
        try {
            await axios.delete(`/api/leaks/${deleteLeakId}/`);
            fetchLeaks();
            setDeleteLeakId(null);
        } catch (err) {
            console.error('Failed to delete leak:', err);
            setError(err.response?.data?.detail || 'Failed to delete leak');
            setDeleteLeakId(null);
        }
    };

    const getSeverityDisplay = (severity) => {
        const option = SEVERITY_OPTIONS.find(s => s.value === severity);
        return option || SEVERITY_OPTIONS[1];
    };

    const getStatusDisplay = (status) => {
        const option = STATUS_OPTIONS.find(s => s.value === status);
        return option || STATUS_OPTIONS[0];
    };

    if (loading) {
        return (
            <div className="flex-grow px-4 py-4 mx-auto w-full max-w-6xl">
                <LoadingContainer />
            </div>
        );
    }

    return (
        <div className="flex-grow px-4 py-4 mx-auto w-full max-w-6xl">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Game Leaks</h1>
                    <p className="text-gray-600 dark:text-gray-400">
                        Track weaknesses in your game and monitor improvement over time
                    </p>
                </div>
                <button
                    onClick={() => setShowAddModal(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                    <PlusIcon className="h-5 w-5" />
                    Add Leak
                </button>
            </div>

            {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
                    <p className="text-red-600 dark:text-red-400">{error}</p>
                </div>
            )}

            {/* Leaks Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {leaks.map(leak => {
                    const severityDisplay = getSeverityDisplay(leak.severity);
                    const statusDisplay = getStatusDisplay(leak.status);

                    return (
                        <div
                            key={leak.id}
                            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow"
                        >
                            <div className="flex items-start justify-between mb-3">
                                <div className="flex-1">
                                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1">{leak.name}</h3>
                                    {leak.category && (
                                        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300">
                                            {leak.category}
                                        </span>
                                    )}
                                </div>
                                <div className="flex gap-1">
                                    <button
                                        onClick={() => handleEdit(leak)}
                                        className="p-1 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                                    >
                                        <PencilIcon className="h-4 w-4" />
                                    </button>
                                    <button
                                        onClick={() => setDeleteLeakId(leak.id)}
                                        className="p-1 text-gray-600 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                                    >
                                        <TrashIcon className="h-4 w-4" />
                                    </button>
                                </div>
                            </div>
                            {leak.symptoms && leak.symptoms.length > 0 && (
                                <div className="mb-2">
                                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Symptoms:</p>
                                    <ul className="text-xs text-gray-600 dark:text-gray-400 list-disc pl-4">
                                        {leak.symptoms.slice(0, 2).map((symptom, idx) => (
                                            <li key={idx} className="line-clamp-1">{symptom}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {leak.cue && (
                                <div className="mb-3 p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded">
                                    <p className="text-xs text-blue-700 dark:text-blue-300 italic line-clamp-2">{leak.cue}</p>
                                </div>
                            )}
                            {/* Occurrence Stats */}
                            <div className="mb-3 p-2 bg-gray-50 dark:bg-gray-700/50 rounded space-y-1">
                                <div className="flex items-center justify-between text-xs">
                                    <span className="text-gray-500 dark:text-gray-400">Total occurrences:</span>
                                    <span className="font-medium text-gray-900 dark:text-white">{leak.hands_count || 0}</span>
                                </div>
                                <div className="flex items-center justify-between text-xs">
                                    <span className="text-gray-500 dark:text-gray-400">Last 30 days:</span>
                                    <div className="flex items-center gap-1">
                                        <span className="font-medium text-gray-900 dark:text-white">{leak.hands_last_30_days || 0}</span>
                                        {leak.trend === 'worsening' && (
                                            <ArrowTrendingUpIcon className="w-3 h-3 text-red-500" title="Worsening" />
                                        )}
                                        {leak.trend === 'improving' && (
                                            <ArrowTrendingDownIcon className="w-3 h-3 text-green-500" title="Improving" />
                                        )}
                                        {leak.trend === 'stable' && (
                                            <MinusIcon className="w-3 h-3 text-gray-400" title="Stable" />
                                        )}
                                    </div>
                                </div>
                                {leak.estimated_cost > 0 && (
                                    <div className="flex items-center justify-between text-xs">
                                        <span className="text-gray-500 dark:text-gray-400">Est. cost:</span>
                                        <span className="font-medium text-red-600 dark:text-red-400">
                                            -{formatCurrency(leak.estimated_cost)}
                                        </span>
                                    </div>
                                )}
                            </div>

                            <div className="flex items-center justify-between text-xs">
                                <span className={`px-2 py-1 rounded ${severityDisplay.bg} ${severityDisplay.color}`}>
                                    {severityDisplay.label}
                                </span>
                                <span className={`px-2 py-1 rounded ${statusDisplay.bg} ${statusDisplay.color}`}>
                                    {statusDisplay.label}
                                </span>
                            </div>
                        </div>
                    );
                })}

                {leaks.length === 0 && (
                    <div className="col-span-full text-center py-12 text-gray-500 dark:text-gray-400">
                        No leaks identified yet. Add your first one to start tracking improvements!
                    </div>
                )}
            </div>

            {/* Add/Edit Modal */}
            {showAddModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 pt-8 overflow-y-auto">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                            {editingLeak ? 'Edit Leak' : 'Add Leak'}
                        </h2>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Name *
                                </label>
                                <input
                                    type="text"
                                    value={form.name}
                                    onChange={(e) => setForm({...form, name: e.target.value})}
                                    className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white"
                                    required
                                    placeholder="e.g., C-betting too wide"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Category
                                </label>
                                <input
                                    type="text"
                                    value={form.category}
                                    onChange={(e) => setForm({...form, category: e.target.value})}
                                    className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white"
                                    placeholder="e.g., Preflop, Postflop, Mental Game"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Symptoms
                                </label>
                                <div className="space-y-2">
                                    {form.symptoms.map((symptom, idx) => (
                                        <div key={idx} className="flex gap-2 items-start">
                                            <span className="flex-1 text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded">
                                                {symptom}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => setForm({...form, symptoms: form.symptoms.filter((_, i) => i !== idx)})}
                                                className="px-2 py-2 text-red-600 hover:text-red-700"
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ))}
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            value={symptomInput}
                                            onChange={(e) => setSymptomInput(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' && symptomInput.trim()) {
                                                    e.preventDefault();
                                                    setForm({...form, symptoms: [...form.symptoms, symptomInput.trim()]});
                                                    setSymptomInput('');
                                                }
                                            }}
                                            className="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white"
                                            placeholder="Add a symptom and press Enter"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (symptomInput.trim()) {
                                                    setForm({...form, symptoms: [...form.symptoms, symptomInput.trim()]});
                                                    setSymptomInput('');
                                                }
                                            }}
                                            className="px-3 py-2 text-sm bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500"
                                        >
                                            Add
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Fix
                                </label>
                                <textarea
                                    value={form.fix}
                                    onChange={(e) => setForm({...form, fix: e.target.value})}
                                    className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white"
                                    rows="2"
                                    placeholder="The specific fix or strategy..."
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Mental Cue
                                </label>
                                <textarea
                                    value={form.cue}
                                    onChange={(e) => setForm({...form, cue: e.target.value})}
                                    className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white"
                                    rows="2"
                                    placeholder="Mental cue or reminder..."
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Description (Optional)
                                </label>
                                <textarea
                                    value={form.description}
                                    onChange={(e) => setForm({...form, description: e.target.value})}
                                    className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white"
                                    rows="2"
                                    placeholder="Additional notes..."
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Severity
                                </label>
                                <select
                                    value={form.severity}
                                    onChange={(e) => setForm({...form, severity: e.target.value})}
                                    className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white"
                                >
                                    {SEVERITY_OPTIONS.map(option => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Status
                                </label>
                                <select
                                    value={form.status}
                                    onChange={(e) => setForm({...form, status: e.target.value})}
                                    className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white"
                                >
                                    {STATUS_OPTIONS.map(option => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex justify-end gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={handleCancel}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
                                >
                                    {editingLeak ? 'Save Changes' : 'Add Leak'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {deleteLeakId && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 pt-8 overflow-y-auto">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Delete Leak</h2>
                        <p className="text-gray-600 dark:text-gray-400 mb-6">
                            Are you sure you want to delete this leak? This action cannot be undone.
                        </p>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setDeleteLeakId(null)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDelete}
                                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default LeaksPage;
